// Geometry for the value-over-time charts (overview net worth, Assets
// tabs, and the mobile app): time-axis layout, value scale, gridlines,
// date ticks, high/low markers and the money-put-in bands. Pure maths
// plus one React hook; no DOM, so both the web and the mobile chart
// components render from it.
import { useMemo } from "react";
import { format, parseISO } from "date-fns";

export const W = 1000; // viewBox width; the SVG stretches to the container
const DAY = 86400000;
// Gridline steps in ₹; the smallest one that fits the range in ≤ 3 steps wins.
const STEPS = [10000, 25000, 50000, 100000, 200000, 250000, 500000, 1e6, 2e6, 5e6, 1e7, 2e7, 5e7, 1e8];

/** "₹45.25L", "₹1.2Cr", "₹80k" — short axis / marker figures (two decimals, zeros trimmed). */
export function formatShort(v) {
  if (v >= 1e7) return "₹" + (v / 1e7).toFixed(2).replace(/\.?0+$/, "") + "Cr";
  if (v >= 1e5) return "₹" + (v / 1e5).toFixed(2).replace(/\.?0+$/, "") + "L";
  return "₹" + Math.round(v / 1000) + "k";
}

// d3 curveMonotoneX: a smooth curve that never overshoots the data.
export function monotonePath(xs, ys) {
  const m = xs.length;
  if (m === 0) return "";
  if (m === 1) return `M${xs[0]},${ys[0]}`;
  const dx = [], dy = [], slope = [];
  for (let i = 0; i < m - 1; i++) {
    dx.push(xs[i + 1] - xs[i]);
    dy.push(ys[i + 1] - ys[i]);
    slope.push(dx[i] === 0 ? 0 : dy[i] / dx[i]);
  }
  const tangent = [slope[0]];
  for (let i = 1; i < m - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) tangent.push(0);
    else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      tangent.push((w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]));
    }
  }
  tangent.push(slope[m - 2]);
  let d = `M${xs[0].toFixed(2)},${ys[0].toFixed(2)}`;
  for (let i = 0; i < m - 1; i++) {
    const h = dx[i] / 3;
    d +=
      `C${(xs[i] + h).toFixed(2)},${(ys[i] + tangent[i] * h).toFixed(2)} ` +
      `${(xs[i + 1] - h).toFixed(2)},${(ys[i + 1] - tangent[i + 1] * h).toFixed(2)} ` +
      `${xs[i + 1].toFixed(2)},${ys[i + 1].toFixed(2)}`;
  }
  return d;
}

// Keep a marker label inside the plot: left-aligned near the left edge,
// right-aligned near the right edge, centred otherwise.
export const edgeShift = (xPct) => (xPct < 12 ? "0" : xPct > 88 ? "-100%" : "-50%");

/**
 * Lays the series out on a true time axis (gaps between snapshots show
 * as gaps), fits the value scale to the period's range, and marks the
 * period's start, high, low and latest point.
 *
 * `base` is an optional second series ({date, value, estimated}) drawn
 * as a gold "money put in" floor under the value line, with a band for
 * the market gain (or loss) between the two.
 */
export function useChartGeometry(points, base, H) {
  return useMemo(() => {
    const n = points.length;
    if (n === 0) return null;
    const baseByDate = base ? new Map(base.map((p) => [p.date, p])) : null;
    const pts = points.map((p) => {
      const b = baseByDate?.get(p.date);
      return { ...p, t: parseISO(p.date).getTime(), b: b ? b.value : null, est: !!b?.estimated };
    });
    const ts = pts.map((p) => p.t);
    const values = pts.map((p) => p.value);
    const t0 = ts[0];
    const t1 = ts[n - 1];
    const tSpan = Math.max(t1 - t0, 1);
    const maxV = Math.max(...values);
    const minV = Math.min(...values);
    const hiIdx = values.indexOf(maxV);
    const loIdx = values.indexOf(minV);
    // The scale fits both series; the high/low markers follow the value line only.
    const bvals = pts.filter((p) => p.b != null).map((p) => p.b);
    const domMax = Math.max(maxV, ...bvals);
    const domMin = Math.min(minV, ...bvals);

    const step = STEPS.find((s) => (domMax - domMin) / s <= 3) || STEPS[STEPS.length - 1];
    let lo = Math.floor(domMin / step) * step;
    let hi = Math.ceil(domMax / step) * step;
    // Breathing room so the line never kisses the top or bottom gridline.
    if (domMin - lo < 0.15 * step) lo -= step;
    if (hi - domMax < 0.15 * step) hi += step;
    if (lo < 0) lo = 0;

    const xOf = (i) => ((ts[i] - t0) / tSpan) * W;
    const yOf = (v) => (1 - (v - lo) / (hi - lo)) * H;
    const xs = points.map((_, i) => xOf(i));
    const line = monotonePath(xs, values.map(yOf));
    const area = `${line} L${xs[n - 1].toFixed(2)},${H} L${xs[0].toFixed(2)},${H} Z`;
    const endY = yOf(values[n - 1]);

    // Money-put-in floor and the gain/loss band above/below it.
    let bands = null;
    const bi0 = pts.findIndex((p) => p.b != null);
    if (baseByDate && bi0 >= 0 && bi0 < n - 1) {
      const bp = pts.slice(bi0);
      const bxs = xs.slice(bi0);
      const bys = bp.map((p) => yOf(p.b));
      const vline = monotonePath(bxs, bp.map((p) => yOf(p.value)));
      const bl = monotonePath(bxs, bys);
      const blRev = monotonePath(bxs.slice().reverse(), bys.slice().reverse()).replace(/^M/, "L");
      const x0 = bxs[0].toFixed(2);
      const x1 = bxs[bxs.length - 1].toFixed(2);
      // Estimated history (before buy prices existed) is drawn dashed.
      const exact0 = bp.findIndex((p) => !p.est);
      const estEnd = exact0 === -1 ? bp.length - 1 : exact0;
      bands = {
        line: exact0 === -1 ? "" : monotonePath(bxs.slice(exact0), bys.slice(exact0)),
        lineEst: estEnd > 0 ? monotonePath(bxs.slice(0, estEnd + 1), bys.slice(0, estEnd + 1)) : "",
        area: `${bl} L${x1},${H} L${x0},${H} Z`,
        band: `${vline} ${blRev} Z`,
        clipAbove: `${bl} L${x1},-20 L${x0},-20 Z`,
        clipBelow: `${bl} L${x1},${H + 20} L${x0},${H + 20} Z`,
        estUntil: exact0 > 0 ? { x: (bxs[exact0] / W) * 100, label: `put in estimated before ${format(parseISO(bp[exact0].date), "d MMM")}` } : null,
      };
    }

    const grid = [];
    for (let v = lo; v <= hi + 1; v += step) {
      const y = yOf(v);
      // A label steps aside when the latest point sits on its gridline.
      grid.push({ y, label: formatShort(v), hidden: Math.abs(y - endY) < 16 });
    }

    // Month boundaries, or weekly / fortnightly dates when the range is short.
    let ticks = [];
    const d0 = new Date(t0);
    for (let m = d0.getUTCMonth(), y = d0.getUTCFullYear(); ; m++) {
      const t = Date.UTC(y + Math.floor(m / 12), m % 12, 1);
      if (t > t1) break;
      if (t < t0) continue;
      const month = format(new Date(t), "MMM");
      const withYear = m % 12 === 0 || ticks.length === 0;
      ticks.push({ t, label: withYear ? `${month} ${format(new Date(t), "yy")}` : month });
    }
    if (ticks.length < 4) {
      const every = tSpan <= 45 * DAY ? 7 : 14;
      ticks = [];
      for (let t = t0; t <= t1; t += every * DAY) ticks.push({ t, label: format(new Date(t), "d MMM") });
    }
    const tickRows = [];
    ticks.forEach((tk) => {
      const x = ((tk.t - t0) / tSpan) * 100;
      if (tickRows.length && x - tickRows[tickRows.length - 1].x < 7) return; // too close to the last one
      tickRows.push({ x, label: tk.label, shift: x < 4 ? "0" : x > 96 ? "-100%" : "-50%" });
    });

    const hiX = (xs[hiIdx] / W) * 100;
    const loX = (xs[loIdx] / W) * 100;
    const hiY = yOf(maxV);
    const loY = yOf(minV);
    return {
      pts, t0, tSpan, xs, yOf, line, area, grid, ticks: tickRows, bands, bi0,
      startY: yOf(values[0]),
      high: { x: hiX, y: hiY, shift: edgeShift(hiX), below: hiY < 26, value: formatShort(maxV), date: format(parseISO(points[hiIdx].date), "d MMM") },
      low: loIdx === hiIdx ? null : { x: loX, y: loY, shift: edgeShift(loX), above: loY > H - 26, value: formatShort(minV), date: format(parseISO(points[loIdx].date), "d MMM") },
      showEnd: hiIdx !== n - 1 && loIdx !== n - 1,
      endY,
    };
  }, [points, base, H]);
}
